/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom'
import { useMedia } from 'react-use'
import { useApolloClient } from '@apollo/client'
import { withSentryRouting } from '@sentry/react'
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  Theme,
  StyledEngineProvider
} from '@mui/material'
import { useAuth as useOIDCAuth } from 'react-oidc-context'
import NavBar from './components/layout/NavBar/NavBar'
import { ModalManager, NotificationManager, ProtectedRoute } from './components/layout'
import TopNav from './components/layout/TopNav'
import { BP_MEDIUM_DOWN } from './lib/styles/breakpoints'
import MobileNavBar from './components/layout/MobileNavBar'
import { ThemeContext, defaultThemeSettings } from './lib/hooks/useTheme'
import { useStyleSettingsQuery } from './generated/scheduler/graphql'
import { AppSettingsContext, defaultAppSettings } from './lib/hooks/useAppSettings'
import getSchedulerClient from './lib/clients/getSchedulerClient'
import useEnvironment from './lib/hooks/useEnvironment'
import PageNotFoundError from './components/shared/errors/PageNotFoundError'
import PageLoadError from './components/shared/errors/PageLoadError'
import LoadBar from './components/shared/loading/LoadBar'
import { Roles } from './types'
import { userIsAdmin, userIsOnlyStudent } from './lib/authenticationUtils'
import useNotification from './lib/hooks/useNotification'
import {
  AdminUserPermission,
  useAppSettingsLazyQuery,
  useApplyTemplateCompleteSubscription
} from './generated/graphql'
import loadElevioClient from './lib/loadElevioClient'
import { updateOwnerPlansCache } from './lib/cacheUtils'
import { useAuth } from './components/provider/OIDCProvider'

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const AddUser = lazy(() => import('./components/users/userManagement/AddUser'))
const AdminUploaderPage = lazy(() => import('./components/users/AdminUploaderPage'))
const BreakDemandReport = lazy(
  () => import('./components/reporting/BreakDemandReport/BreakDemandReportPage')
)
const CourseDemandReport = lazy(
  () => import('./components/reporting/CourseDemandReport/CourseDemandReportWrapper')
)
const PlannedCourseDemandReport = lazy(
  () =>
    import('./components/reporting/PlanningCourseDemandReport/PlanningCourseDemandReportWrapper')
)
const PlanCreationDataReport = lazy(
  () => import('./components/reporting/PlanCreationDataReport/PlanCreationDataReportWrapper')
)
const CoursePage = lazy(() => import('./components/search/courses/CoursePage/CoursePage'))
const CourseHistoryPage = lazy(() => import('./components/student/courseHistory'))
const DiagnosticsPage = lazy(() => import('./components/diagnostics/Diagnostics'))
const InstructorCoursesPage = lazy(
  () => import('./components/search/courses/InstructorCoursesPage/InstructorCoursesPage')
)
const Dashboard = lazy(() => import('./components/dashboard/DashboardWrapper'))
const StudentEngagementDetail = lazy(
  () => import('./components/dashboard/engagement/detail/StudentEngagementDetail')
)
const EditUser = lazy(() => import('./components/users/userManagement/EditUser'))
const Login = lazy(() => import('./components/authentication/Login/LoginWrapper'))
const MyPlansPage = lazy(() => import('./components/plan/planList/MyPlans/MyPlansPage'))
const NewPlanPage = lazy(() => import('./components/plan/new/NewPlan'))
const PlaceholderBuilderPage = lazy(
  () =>
    import(
      './components/templates/placeholderTemplates/placeholderTemplateBuilder/PlaceholderBuilder'
    )
)
const PlanBoardPage = lazy(() => import('./components/plan/board/PlanBoard/PlanBoardPage'))
const PreloaderHistory = lazy(() => import('./components/preloader'))
const PreloaderWizard = lazy(() => import('./components/preloader/Preloader'))
const Profile = lazy(() => import('./components/users/profile/Profile'))
const RegistrationWorksheetPage = lazy(
  () => import('./components/registrationWorksheet/RegistrationWorksheet')
)
const ReportsPage = lazy(() => import('./components/reporting/ReportsPage/ReportsPage'))
const RequirementsPage = lazy(() => import('./components/requirements/RequirementsPage'))
const ResourcePage = lazy(() => import('./components/resource/ResourcePage/ResourcePage'))
const SearchHomePage = lazy(() => import('./components/search/SearchHomePage/SearchHomePage'))
const SectionPage = lazy(() => import('./components/search/sections/SectionPage/SectionPage'))
const StudentNewPlanPage = lazy(() => import('./components/student/StudentNewPlanPage'))
const StudentPage = lazy(() => import('./components/student/StudentPage'))
const StudentUsageReport = lazy(
  () =>
    import('./components/reporting/studentUsageReport/StudentUsageReport/StudentUsageReportWrapper')
)
const TemplateBuilderPage = lazy(
  () => import('./components/templates/planTemplates/planTemplateBuilder/TemplateBuilder')
)
const TemplatesPage = lazy(() => import('./components/templates/Templates'))
const TemplateWizard = lazy(() => import('./components/templates/planTemplates/applyTemplate'))
const UserManagementPage = lazy(
  () => import('./components/users/userManagement/UserManagementPage/UserManagementPage')
)
const VideoPlayer = lazy(() => import('./components/resource/VideoPlayer/VideoPlayer'))

// Create Custom Sentry Route component for track parameterized transaction names (i.e. the paths)
const SentryRoute = withSentryRouting(Route)
const muiTheme = createTheme()

type LocationState = {
  correlationId?: string | null
}

const appCss = css`
  display: flex;
  flex-flow: row;
  height: 100%;
`

const helpContainerCss = css`
  background-color: #1a1a1a;
  min-height: 100%;
  position: absolute;
  width: 100%;
`

const App = () => {
  const { state } = useLocation<LocationState>()
  const [allCorrelationIds, setAllCorrelationIds] = useState<string[]>([])
  const { isAuthenticated, isInitializing, user } = useAuth()
  const { removeUser, signoutRedirect, user: oidcUser } = useOIDCAuth()

  const location = useLocation()
  const [isSideNavExpanded, setIsSideNavExpanded] = useState(false)
  const [isSideNavPersisted, setIsSideNavPersisted] = useState(false)
  const [displaySkipLink, setDisplaySkipLink] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false)
  const isMedium = useMedia(`(${BP_MEDIUM_DOWN})`)
  const mainContent: any = useRef<HTMLElement>(null)
  const environment = useEnvironment()
  const client = useApolloClient()
  const {
    data,
    loading: styleLoading,
    error: styleError
  } = useStyleSettingsQuery({
    variables: { environment },
    client: getSchedulerClient(client)
  })
  const [getAppSettings, { data: appSettingsData }] = useAppSettingsLazyQuery()
  const { hideNotif, showSuccessNotif } = useNotification()
  const isHelpPage = useRouteMatch('/help')
  const isSearchPage = useRouteMatch('/search')
  const isTemplate = useRouteMatch('/templates')
  const isPreloader = useRouteMatch('/preloader')
  const isTablePage = isTemplate || isPreloader

  useEffect(() => {
    hideNotif()
  }, [location.pathname, hideNotif])
  useEffect(() => {
    if (isAuthenticated && user) {
      // assign global for tracking purposes in google analytics
      // @ts-ignore
      window.isAdvisorOrAdmin = userIsAdmin(user)
    } else {
      // @ts-ignore
      window.isAdvisorOrAdmin = false
    }
  }, [isAuthenticated, user])
  useEffect(() => {
    if (isAuthenticated) {
      getAppSettings()
    }
  }, [getAppSettings, isAuthenticated])
  useEffect(() => {
    if (isAuthenticated && user && appSettingsData) {
      const { disableElevio, disableElevioForStudents } =
        appSettingsData.siteSettings.academicPlanningSettings
      const elevioDisabled = disableElevio || (disableElevioForStudents && userIsOnlyStudent(user))
      if (!elevioDisabled) {
        loadElevioClient(user)
      }
    }
  }, [appSettingsData, isAuthenticated, user])

  useEffect(() => {
    if (state?.correlationId) {
      setAllCorrelationIds([...allCorrelationIds, state?.correlationId])
      state.correlationId = null
    }
  }, [allCorrelationIds, state])

  useApplyTemplateCompleteSubscription({
    variables: {
      correlationIds: allCorrelationIds!
    },
    // skip sub until correlationId is set by mutation
    skip: !allCorrelationIds?.length,
    onData: ({ client: apolloClient, data: subscriptionData }) => {
      if (subscriptionData?.data?.applyTemplateComplete) {
        const {
          data: { applyTemplateComplete }
        } = subscriptionData

        if (applyTemplateComplete.plansCreated) {
          // Update the cache for each created plan
          applyTemplateComplete.plansCreated.forEach((plan) =>
            updateOwnerPlansCache(apolloClient.cache, plan.ownerAppUserId, plan)
          )

          // Display only the first 25 characters of the template title
          let templateTitle = applyTemplateComplete.templateTitle || 'Template'
          if (templateTitle.length > 25) {
            templateTitle = `${templateTitle.substring(0, 25)}...`
          }

          showSuccessNotif(
            `${templateTitle} applied to ${applyTemplateComplete?.studentCount} students`
          )
        }
        // Remove this correlationId
        setAllCorrelationIds(
          allCorrelationIds.filter(
            (c) => c !== subscriptionData?.data?.applyTemplateComplete.correlationId
          )
        )
      }
    }
  })

  if (styleLoading || isInitializing || isLoggingOut) {
    return <LoadBar persist />
  }

  if (styleError) {
    return <PageLoadError />
  }

  const toggleNavPersist = () => setIsSideNavPersisted(!isSideNavPersisted)
  const onExpand = () => setIsSideNavExpanded(true)

  const onLogout = async (options?: { federated?: boolean; providerType?: any }) => {
    // Set loading state to inform UX during logout
    setIsLoggingOut(true)
    // Clear Apollo Client cache
    await client.clearStore()
    await signoutRedirect({
      extraQueryParams: {
        id_token_hint: oidcUser?.id_token || ''
      }
    })
    // Clear local session data
    await removeUser()
    localStorage.clear()
  }

  const onShrink = () => setIsSideNavExpanded(false)
  const onSkipLinkFocus = () => setDisplaySkipLink(true)
  const onSkipLinkBlur = () => setDisplaySkipLink(false)
  const onSkipLinkClick = () => {
    mainContent.current.setAttribute('tabIndex', '-1')
    mainContent.current.focus()
    mainContent.current.removeAttribute('tabIndex')
  }
  const showDashboard = isAuthenticated && userIsAdmin(user)

  const contentCss = css`
    flex: 1 1 auto;
    ${!isMedium && !isHelpPage && isAuthenticated && 'margin-left: 80px;'}
    ${isSideNavPersisted && 'margin-left: 264px;'}
    min-height: 0px;
    transition: margin-left 0.3s ease;
  `
  const mainCss = css`
    height: calc(100% - 57px);
    outline: 0;
  `
  const tableContainerCss = css`
    height: calc(100% - 69px);
    outline: 0;
  `
  const studentPaths = [
    '/students/:appUserId/plans/:planId?',
    '/students/:appUserId/course-history',
    '/students/:appUserId/requirements',
    '/students/:appUserId/schedule'
  ]
  const diagnosticPaths = [
    '/diagnostics/logs',
    '/diagnostics/raw-request',
    '/diagnostics/settings',
    '/diagnostics/'
  ]
  const templatesPaths = ['/templates/plan', '/templates/placeholder', '/templates/']

  const getMainCss = () => {
    if (isHelpPage) {
      return helpContainerCss
    }
    if (isTablePage) {
      return tableContainerCss
    }
    return mainCss
  }

  const getTheme = () => {
    let theme = data?.environment?.publicSettings?.styleSettings || defaultThemeSettings

    // Default the navbarColor to the primary color if there isn't one set
    if (!theme.navbarColor) {
      theme = { ...theme }
      theme.navbarColor = theme.primaryColor
    }

    return theme
  }

  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={muiTheme}>
        <ThemeContext.Provider value={getTheme()}>
          <AppSettingsContext.Provider value={appSettingsData?.siteSettings || defaultAppSettings}>
            <div css={appCss}>
              {isMedium
                ? !isHelpPage &&
                  isAuthenticated && (
                    <MobileNavBar
                      isAuthenticated={isAuthenticated}
                      isExpanded={isSideNavExpanded}
                      onExpand={onExpand}
                      onShrink={onShrink}
                      user={user}
                    />
                  )
                : !isHelpPage &&
                  isAuthenticated && (
                    <NavBar
                      displaySkipLink={displaySkipLink}
                      onSkipLinkFocus={onSkipLinkFocus}
                      onSkipLinkBlur={onSkipLinkBlur}
                      onSkipLinkClick={onSkipLinkClick}
                      isAuthenticated={isAuthenticated}
                      isExpanded={isSideNavExpanded}
                      isPersisted={isSideNavPersisted}
                      onExpand={onExpand}
                      onShrink={onShrink}
                      toggleIsPersisted={toggleNavPersist}
                      user={user}
                    />
                  )}
              <div css={contentCss}>
                {!isHelpPage && (isAuthenticated || isSearchPage) && (
                  <TopNav
                    onExpand={onExpand}
                    onLogout={onLogout}
                    isAuthenticated={isAuthenticated}
                    user={user}
                  />
                )}
                <main role="main" ref={mainContent} css={getMainCss()}>
                  <Suspense fallback={<div aria-hidden />}>
                    <Switch>
                      <ProtectedRoute path="/" exact>
                        {showDashboard ? <Dashboard /> : <MyPlansPage isLandingPage />}
                      </ProtectedRoute>
                      <ProtectedRoute path="/dashboard" exact roles={[Roles.admin]}>
                        <Dashboard />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/dashboard/:engagementOpportunity/:termCode?/:qualifier?"
                        exact
                        roles={[Roles.admin]}
                      >
                        <StudentEngagementDetail />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path={diagnosticPaths}
                        permissions={AdminUserPermission.Diagnostics}
                      >
                        <DiagnosticsPage />
                      </ProtectedRoute>
                      <ProtectedRoute path="/plans" exact>
                        <MyPlansPage />
                      </ProtectedRoute>
                      <SentryRoute
                        path={[
                          '/login:errorType?',
                          '/login/civitas',
                          '/login/auth0',
                          '/login/oidc',
                          '/login:oidc',
                          '/login:auth0'
                        ]}
                        exact
                      >
                        <Login isLoggingOut={isLoggingOut} />
                      </SentryRoute>
                      <SentryRoute path="/search" exact>
                        <SearchHomePage />
                      </SentryRoute>
                      <SentryRoute path="/search/courses/:courseId" exact>
                        <CoursePage />
                      </SentryRoute>
                      <SentryRoute path="/search/courses/:courseId/sections/:sectionId">
                        <SectionPage />
                      </SentryRoute>
                      <SentryRoute path="/search/terms/:termCode/instructors/:instructorName">
                        <InstructorCoursesPage />
                      </SentryRoute>
                      <ProtectedRoute path="/help/:video">
                        <VideoPlayer />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/preloader"
                        exact
                        permissions={AdminUserPermission.PreloadStudentCourses}
                      >
                        <PreloaderHistory />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/preloader/create"
                        permissions={AdminUserPermission.PreloadStudentCourses}
                      >
                        <PreloaderWizard />
                      </ProtectedRoute>
                      <ProtectedRoute path="/profile">
                        <Profile />
                      </ProtectedRoute>
                      <ProtectedRoute path="/plans/new">
                        <NewPlanPage />
                      </ProtectedRoute>
                      <ProtectedRoute path="/plans/:planId">
                        <PlanBoardPage ownerAppUserId={user?.appUserId || 0} />
                      </ProtectedRoute>
                      <ProtectedRoute path="/course-history" exact>
                        <CourseHistoryPage appUserId={user?.appUserId || 0} />
                      </ProtectedRoute>
                      <ProtectedRoute
                        exact
                        path="/reports"
                        permissions={[
                          AdminUserPermission.CourseDemandRawData,
                          AdminUserPermission.StudentUsageRawData,
                          AdminUserPermission.StudentUsageReports
                        ]}
                      >
                        <ReportsPage />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/reports/break-demand-report"
                        permissions={AdminUserPermission.CourseDemandRawData}
                      >
                        <BreakDemandReport />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/reports/course-demand-report"
                        permissions={AdminUserPermission.CourseDemandRawData}
                      >
                        <CourseDemandReport />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/reports/planning-course-demand-report"
                        permissions={AdminUserPermission.PlanningCourseDemandData}
                      >
                        <PlannedCourseDemandReport />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/reports/plan-creation-data-report"
                        permissions={AdminUserPermission.PlanCreationData}
                      >
                        <PlanCreationDataReport />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/reports/student-usage-report"
                        permissions={[
                          AdminUserPermission.StudentUsageReports,
                          AdminUserPermission.StudentUsageRawData
                        ]}
                      >
                        <StudentUsageReport />
                      </ProtectedRoute>
                      <ProtectedRoute path="/resources">
                        <ResourcePage />
                      </ProtectedRoute>
                      <ProtectedRoute path="/students/:appUserId/plans/new" roles={[Roles.admin]}>
                        <StudentNewPlanPage />
                      </ProtectedRoute>
                      <ProtectedRoute path={studentPaths} roles={[Roles.admin]}>
                        <StudentPage />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/templates/plan/:templateId/apply"
                        roles={[Roles.admin]}
                      >
                        <TemplateWizard />
                      </ProtectedRoute>
                      <ProtectedRoute path="/templates/plan/:templateId" roles={[Roles.admin]}>
                        <TemplateBuilderPage />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/templates/placeholder/:placeholderId"
                        roles={[Roles.admin]}
                      >
                        <PlaceholderBuilderPage />
                      </ProtectedRoute>
                      <ProtectedRoute path={templatesPaths} roles={[Roles.admin]}>
                        <TemplatesPage />
                      </ProtectedRoute>
                      <ProtectedRoute path="/requirements" exact>
                        <RequirementsPage appUserId={user?.appUserId || 0} />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/users"
                        exact
                        permissions={AdminUserPermission.ManageUsers}
                      >
                        <UserManagementPage />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/users/new"
                        permissions={AdminUserPermission.ManageUsers}
                      >
                        <AddUser />
                      </ProtectedRoute>
                      <ProtectedRoute path="/users/upload-csv" roles={[Roles.civitasEmployee]}>
                        <AdminUploaderPage />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/users/edit/:userId"
                        permissions={AdminUserPermission.ManageUsers}
                      >
                        <EditUser />
                      </ProtectedRoute>
                      <ProtectedRoute
                        path="/worksheet"
                        permissions={AdminUserPermission.RegistrationWorksheet}
                      >
                        <RegistrationWorksheetPage />
                      </ProtectedRoute>
                      {/** Fallback route, since these are in a Switch */}
                      <SentryRoute>
                        <PageNotFoundError isAuthenticated={isAuthenticated} />
                      </SentryRoute>
                    </Switch>
                  </Suspense>
                </main>
              </div>
              <ModalManager />
              <NotificationManager />
            </div>
          </AppSettingsContext.Provider>
        </ThemeContext.Provider>
      </MuiThemeProvider>
    </StyledEngineProvider>
  )
}

// Why React.memo here: https://twitter.com/sophiebits/status/1228955237655834624
export default React.memo(App)
